import FilterAutocomplete from '@app/src/pages/ResourceCollection/Filters/FilterAutocomplete'
import type { Option } from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import { palette } from '@app/src/theme/palette'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Box, Checkbox, Chip, Switch, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface NegatableSelectProps {
  options: Option[]
  label: string
  positiveValues: string[]
  negativeValues: string[]
  onUpdate: (positive: string[], negative: string[]) => void
  onToggleNegation: (optionId: string) => void
}

const NegatableSelect = ({
  options,
  label,
  positiveValues,
  negativeValues,
  onUpdate,
  onToggleNegation,
}: NegatableSelectProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const selectedOptions = options.filter(
    o => positiveValues.includes(o.value.toString()) || negativeValues.includes(o.value.toString()),
  )

  return (
    <FilterAutocomplete
      multiple
      size="small"
      fieldLabel={label}
      options={options}
      value={selectedOptions}
      isOptionEqualToValue={(option, value) => option.value.toString() === value.value.toString()}
      onChange={(_event, _newValue, reason, details) => {
        if (reason === 'clear') {
          onUpdate([], [])
          return
        }
        const optionId = details?.option?.value?.toString()
        if (!optionId) return

        if (reason === 'selectOption') {
          onUpdate([...positiveValues, optionId], negativeValues)
        } else if (reason === 'removeOption') {
          onUpdate(
            positiveValues.filter(v => v !== optionId),
            negativeValues.filter(v => v !== optionId),
          )
        }
      }}
      renderOption={(optionProps, option, { selected }) => {
        const { key: _omitKey, ...rest } = optionProps as Omit<typeof optionProps, 'key'> & { key?: React.Key }
        const optionId = option.value.toString()
        const isNegative = negativeValues.includes(optionId)

        return (
          <li key={optionId} {...rest} style={{ ...rest.style, display: 'flex', alignItems: 'center' }}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon color="primary" />}
              checkedIcon={<CheckBoxIcon />}
              style={{ marginRight: 8 }}
              checked={selected}
              disableRipple
              tabIndex={-1}
            />
            <Box sx={{ flex: 1 }}>{option.label}</Box>
            {selected && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', ml: 1 }}
                onClick={e => {
                  e.stopPropagation()
                  onToggleNegation(optionId)
                }}
              >
                <Switch
                  size="small"
                  checked={isNegative}
                  tabIndex={-1}
                  aria-label={formatMessage({ id: 'filters.negatable.toggleNegationAriaLabel' })}
                />
                <Typography variant="caption" noWrap>
                  {formatMessage({ id: 'filters.negatable.isNot' })}
                </Typography>
              </Box>
            )}
          </li>
        )
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const isNegative = negativeValues.includes(option.value.toString())
          const { key, ...tagProps } = getTagProps({ index })
          return (
            <Chip
              key={key}
              label={
                isNegative
                  ? formatMessage({ id: 'filters.negatable.isNotChip' }, { label: option.label })
                  : option.label
              }
              size="small"
              sx={isNegative ? { backgroundColor: palette.error.main } : {}}
              {...tagProps}
            />
          )
        })
      }
    />
  )
}

export default NegatableSelect
